import { Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, Validators } from '@angular/forms';
import { LoadingController, NavParams, PopoverController, ToastController } from '@ionic/angular';
import { product } from 'src/app/models/interfaces';
import { CommonService } from 'src/app/services/common.service';
import { FirestoreService } from 'src/app/services/firestore.service';
import { StorageService } from 'src/app/services/storage.service';

@Component({
  selector: 'app-edit-products',
  templateUrl: './edit-products.component.html',
  styleUrls: ['./edit-products.component.scss'],
})
export class EditProductsComponent implements OnInit {
  producto : any;
  newImage: string = '';
  load: any;

  // ─── Imágenes ───────────────────────────────────────────────────────────────
  // Cada entrada de imagen pendiente de subir incluye el File y el género asignado
  arrayImgSubir: { file: File; gender: 'hombre' | 'mujer' | 'unisex' }[] = [];
  // Preview local de las imágenes pendientes (para mostrar en la UI)
  arrayImagenesPreview: { src: string; gender: 'hombre' | 'mujer' | 'unisex' }[] = [];
  // Género temporal seleccionado ANTES de elegir la foto (se resetea tras agregar)
  pendingImgGender: 'hombre' | 'mujer' | 'unisex' = 'unisex';

  path = 'productos/';
  generos: any[];
  categorias: any[];
  styles: any[];
  sizes: any[];

  objUrlFile: File | null = null;
  mtlUrlFile: File | null = null;
  objUrlPreview: boolean = false;
  mtlUrlPreview: boolean = false;
  objUrlName: string = '';
  mtlUrlName: string = '';
  mostrarCampos3D: boolean = false;
  selectedColor: string = '#000000';
  moldeImgFile: File | null = null;
  moldeImgPreview: string | null = null;

  // Opciones de género para el tag de imágenes
  genderOptions: ('hombre' | 'mujer' | 'unisex')[] = ['hombre', 'mujer', 'unisex'];

  get name() { return this.formProduct.get('name'); }
  get price() { return this.formProduct.get('price'); }
  get description() { return this.formProduct.get('description'); }
  get image() { return this.formProduct.get('image'); }
  get gender() { return this.formProduct.get('gender'); }
  get category() { return this.formProduct.get('category'); }
  get style() { return this.formProduct.get('style'); }

  constructor(
    private storage: StorageService,
    private popocerCrl: PopoverController,
    private navParams: NavParams,
    private fb: FormBuilder,
    private toast: ToastController,
    private firestore: FirestoreService,
    private common: CommonService
  ) {
    this.producto = { gender: [] };
  }

  formProduct = this.fb.group({
    price: [null, [Validators.required]],
    name: [null, [Validators.required]],
    // ✅ gender ahora es array para soportar unisex / multi-género
    gender: [[] as string[], [Validators.required]],
    category: [null, [Validators.required]],
    style: [[] as string[], [Validators.required]],
    priceBefore: [null],
    // ✅ image se guarda como array de { url, gender }
    image: [null, [Validators.required]],
    description: [null, [Validators.required]],
    id: [null],
    date: new Date(),
    offer: [false],
    colors: [false],
    sizes: [null, [Validators.required]],
    objUrl: [null],
    mtlUrl: [null],
    availableColors: this.fb.array([]),
    imagenMolde: [null],
  });

  get availableColors() {
    return this.formProduct.get('availableColors') as FormArray;
  }

  ngOnInit() {
    // Detectar si se selecciona estilo 3D
    this.formProduct.get('style').valueChanges.subscribe(value => {
      this.mostrarCampos3D = value?.includes('3d');

      if (this.mostrarCampos3D) {
        this.formProduct.get('objUrl').setValidators([Validators.required]);
        this.formProduct.get('mtlUrl').setValidators([Validators.required]);
      } else {
        this.formProduct.get('objUrl').clearValidators();
        this.formProduct.get('mtlUrl').clearValidators();
      }

      this.formProduct.get('objUrl').updateValueAndValidity();
      this.formProduct.get('mtlUrl').updateValueAndValidity();
    });

    // Cargar producto existente
    this.producto = this.navParams.get('producto');
    if (this.producto) {
      this.formProduct.patchValue(this.producto);

      if (this.producto.availableColors?.length > 0) {
        this.availableColors.clear();
        this.producto.availableColors.forEach((item: any) => {
          const colorValue = typeof item === 'string' ? item : item.color;
          this.availableColors.push(this.fb.control(colorValue, Validators.required));
        });
      }
    }

    // Limpiar/poblar colores cuando cambia el toggle
    this.formProduct.get('colors')?.valueChanges.subscribe((isMulticolor) => {
      if (isMulticolor) {
        if (this.availableColors.length === 0) this.addColor();
      } else {
        this.availableColors.clear();
      }
    });

    this.getGender();
    this.getCategory();
    this.getEstyles();
    this.getSize();
  }

  getSize() {
    this.firestore.getCollection('sizes/').subscribe(res => {
      this.sizes = res;
      this.sizes.sort((a, b) => (a.number < b.number ? 1 : a === b ? 0 : -1));
    });
  }

  getGender() {
    this.firestore.getCollection('genero/').subscribe(res => {
      this.generos = res;
    });
  }

  getCategory() {
    this.firestore.getCollection('categorias/').subscribe(res => {
      this.categorias = res;
    });
  }

  getEstyles() {
    this.firestore.getCollection('styles/').subscribe(res => {
      this.styles = res;
    });
  }

  // ─── Gestión de colores ──────────────────────────────────────────────────────

  addColor() {
    if (this.selectedColor) {
      this.availableColors.push(this.fb.control(this.selectedColor, Validators.required));
      this.selectedColor = '#000000';
    }
  }

  onColorChange(event: any) {
    this.selectedColor = event.target.value;
  }

  removeColor(index: number) {
    this.availableColors.removeAt(index);
  }

  // ─── Gestión de imágenes con género ─────────────────────────────────────────

  /**
   * Se llama cuando el usuario elige una foto.
   * Se usa `pendingImgGender` como género asignado a esa foto.
   */
  async newImg(photo: any) {
    try {
      const file: File = photo.target.files[0];
      if (!file) return;

      this.arrayImgSubir.push({ file, gender: this.pendingImgGender });

      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.arrayImagenesPreview.push({
          src: e.target.result as string,
          gender: this.pendingImgGender,
        });
      };
      reader.readAsDataURL(file);

      // Reset género pendiente a 'unisex' para la siguiente foto
      this.pendingImgGender = 'unisex';
    } catch (error) {
      this.formProduct.get('image').setValue(null);
      this.presentToast('No se puede cargar la foto: ' + error);
    }
  }

  /**
   * Cambia el género de una imagen pendiente (aún no subida).
   */
  changePreviewGender(index: number, gender: 'hombre' | 'mujer' | 'unisex') {
    this.arrayImagenesPreview[index].gender = gender;
    this.arrayImgSubir[index].gender = gender;
  }

  /**
   * Cambia el género de una imagen ya guardada en Firebase.
   */
  changeStoredImgGender(index: number, gender: 'hombre' | 'mujer' | 'unisex') {
    this.producto.image[index].gender = gender;
  }

  deletePreviewImg(index: number) {
    this.arrayImagenesPreview.splice(index, 1);
    this.arrayImgSubir.splice(index, 1);
  }

  removeImg(img: any, i: number) {
    this.producto['image'].splice(i, 1);
  }

  // ─── Archivos 3D ────────────────────────────────────────────────────────────

  uploadMoldeImg(event: any) {
    const file = event.target.files[0];
    if (file) {
      this.moldeImgFile = file;
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.moldeImgPreview = e.target.result;
      };
      reader.readAsDataURL(file);
    }
  }

  uploadobjUrl(event: any) {
    if (event.target.files?.[0]) {
      this.objUrlFile = event.target.files[0];
      this.objUrlPreview = true;
      this.objUrlName = this.objUrlFile.name;
    }
  }

  uploadmtlUrl(event: any) {
    if (event.target.files?.[0]) {
      this.mtlUrlFile = event.target.files[0];
      this.mtlUrlPreview = true;
      this.mtlUrlName = this.mtlUrlFile.name;
    }
  }

  // ─── Guardar producto ────────────────────────────────────────────────────────

  async saveProduct() {
    await this.common.showLoading('Cargando');

    try {
      const style = this.formProduct.get('style')?.value;

      // Subir archivos 3D si aplica
      if (Array.isArray(style) && style.includes('3d')) {
        const uploadPromises = [];

        if (this.objUrlFile) {
          const name = this.objUrlFile.name + '_' + Date.now();
          uploadPromises.push(
            this.storage.uploadImg(this.objUrlFile, 'modelos3d/objetos', name).then(url =>
              this.formProduct.get('objUrl').setValue(url)
            )
          );
        }

        if (this.mtlUrlFile) {
          const name = this.mtlUrlFile.name + '_' + Date.now();
          uploadPromises.push(
            this.storage.uploadImg(this.mtlUrlFile, 'modelos3d/materiales', name).then(url =>
              this.formProduct.get('mtlUrl').setValue(url)
            )
          );
        }

        if (this.moldeImgFile) {
          const name = 'molde_' + this.moldeImgFile.name + '_' + Date.now();
          uploadPromises.push(
            this.storage.uploadImg(this.moldeImgFile, 'modelos3d/molde', name).then(url =>
              this.formProduct.get('imagenMolde')?.setValue(url)
            )
          );
        }

        await Promise.all(uploadPromises);
      }

      // Si hay imágenes nuevas pendientes, quitar validación del campo image
      if (this.arrayImgSubir.length > 0) {
        this.formProduct.controls['image'].clearValidators();
        this.formProduct.controls['image'].updateValueAndValidity();
      }

      if (this.formProduct.valid) {
        const isExisting = !!this.formProduct.get('id').value;

        // Subir imágenes nuevas con su género asignado
        const newImages: { url: string; gender: string }[] = [];
        for (const item of this.arrayImgSubir) {
          const name = item.file.name + '_' + Date.now();
          const url = await this.storage.uploadImg(item.file, 'productos', name);
          newImages.push({ url, gender: item.gender });
        }

        if (isExisting) {
          // Producto existente: concatenar nuevas imágenes con las guardadas
          const existingImages = this.producto.image ?? [];
          // Normalizar imágenes viejas que no tengan género (legado)
          const normalized = existingImages.map((img: any) =>
            img.gender ? img : { ...img, gender: 'unisex' }
          );
          (this.formProduct.get('image') as any).setValue([...normalized, ...newImages]);

          await this.firestore.updateDoc(
            this.formProduct.value,
            this.path,
            this.formProduct.get('id').value
          );
          this.presentToast('Producto editado');
        } else {
          // Producto nuevo
          const newId = this.firestore.createId();
          this.formProduct.get('id').setValue(newId);
          (this.formProduct.get('image') as any).setValue(newImages);

          await this.firestore.createDoc(this.formProduct.value, this.path, newId);
          this.presentToast('Producto creado');
        }

        this.dismissClick();
      } else {
        this.formProduct.markAllAsTouched();
      }
    } catch (error) {
      console.error(error);
      this.presentToast('Ocurrió un error. Verifica tu conexión.');
    } finally {
      this.common.load.dismiss();
    }
  }

  async presentToast(mensaje: string) {
    const toast = await this.toast.create({
      message: mensaje,
      duration: 500,
      color: 'light',
    });
    toast.present();
  }

  ngOnDestroy(): void {
    this.producto = null;
  }

  async dismissClick() {
    await this.popocerCrl.dismiss();
  }
}
